//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import { Button, ButtonTypes } from 'common/components/core/button';
import DismissiblePopoverTheme from 'common/components/core/popover/DismissiblePopover.scss';

import type { PopoverPlaceEnumType } from 'common/components/core/popover/popOverFactory';

/*========================================
=            Type Definitions            =
========================================*/
type State = {
    active: boolean
};

type DefaultProps = {
    actionLabel: string
};

type Props = {
    active: boolean,
    theme: Object,
    actionLabel: string,
    onPopoverClose: Function
};

type dismissablePopoverFactoryParams = {
    InnerComponent:
        | React.Component<*, *>
        | ((params: defaultInnerComponentPropType) => React.Element<any>),
    Actions:
        | React.Component<*, *>
        | ((params: defaultActionsPropType) => React.Element<any>),
    popoverPlace?: PopoverPlaceEnumType
};

type defaultActionsPropType = {
    theme: Object,
    actionLabel: string,
    onActionClick: Function
};

type defaultInnerComponentPropType = {
    theme: Object,
    text?: any
};
/*=====  End of Type Definitions  ======*/

const DefaultActions = ({
    actionLabel,
    onActionClick,
    theme
}: defaultActionsPropType): React.Element<any> => {
    return (
        <Button
            label={actionLabel}
            theme={theme}
            buttonType={ButtonTypes.Text}
            onClick={onActionClick}
            icon="cancel-thin"
            ripple={false}
        />
    );
};

const DefaultInnerComponent = ({
    text = '',
    theme
}: defaultInnerComponentPropType): React.Element<any> => {
    return <div className={theme.content}>{text}</div>;
};

const dismissablePopoverFactory = ({
    InnerComponent = DefaultInnerComponent,
    Actions = DefaultActions,
    popoverPlace = PopoverPlaceEnum.Below
}: dismissablePopoverFactoryParams): React.Component<*, *> => {
    class DismissablePopover extends React.PureComponent<Props, State> {
        props: Props;
        state: State;
        DefaultProps: DefaultProps;
        static defaultProps = {
            actionLabel: 'Got it!'
        };
        constructor(props: Props) {
            super(props);
            this.state = {
                active: props.active || false
            };
        }

        UNSAFE_componentWillReceiveProps(nextProps: Props) {
            const { active: nextActive } = nextProps;
            const { active, onPopoverClose } = this.props;
            if (nextActive !== active) {
                this.setState({ active: nextActive }, () => {
                    onPopoverClose(this.state.active);
                });
            }
        }

        togglePopover = () => {
            const { onPopoverClose } = this.props;
            const { active } = this.state;
            this.setState({ active: !active }, () => {
                onPopoverClose(this.state.active);
            });
        };

        getPopoverContent = () => {
            const { theme, actionLabel } = this.props;
            return (
                <div className={theme.popoverContent}>
                    <InnerComponent {...this.props} />
                    <div className={theme.actionsWrap}>
                        <Actions
                            onActionClick={this.togglePopover}
                            theme={theme}
                            actionLabel={actionLabel}
                        />
                    </div>
                </div>
            );
        };

        render() {
            const { theme } = this.props;
            const { active } = this.state;
            return (
                <DivWithPopover
                    theme={theme}
                    place={popoverPlace}
                    className={theme.popoverWrap}
                    popOverContent={this.getPopoverContent()}
                    isPopOverOpen={active}
                />
            );
        }
    }

    return themr(
        'DismissablePopover',
        DismissiblePopoverTheme
    )(DismissablePopover);
};

export default dismissablePopoverFactory({
    InnerComponent: DefaultInnerComponent,
    Actions: DefaultActions
});
export { dismissablePopoverFactory };
